var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
const ALL_SCOPES = ['db', 'schema', 'group', 'key', 'version'];
const SCOPE_KEYWORDS = new Set(ALL_SCOPES);
export class AliasTable {
    constructor() {
        this.tables = new Map();
    }
    set(scope, name, hash) {
        let table = this.tables.get(scope);
        if (table === undefined) {
            table = new Map();
            this.tables.set(scope, table);
        }
        table.set(name, hash);
    }
    get(scope, name) {
        var _a;
        return (_a = this.tables.get(scope)) === null || _a === void 0 ? void 0 : _a.get(name);
    }
    delete(scope, name) {
        var _a;
        return ((_a = this.tables.get(scope)) === null || _a === void 0 ? void 0 : _a.delete(name)) === true;
    }
    list(scope) {
        const scopes = scope === undefined ? ALL_SCOPES : [scope];
        const out = [];
        for (const s of scopes) {
            const table = this.tables.get(s);
            if (table === undefined)
                continue;
            for (const [name, hash] of table)
                out.push({ scope: s, name, hash });
        }
        return out;
    }
}
export function isAliasScope(text) {
    return SCOPE_KEYWORDS.has(text);
}
export function resolveAliasTarget(scope, prefix, session) {
    return __awaiter(this, void 0, void 0, function* () {
        const normalized = prefix.startsWith('#') ? prefix.slice(1) : prefix;
        const searchScopes = scope === 'auto' ? ALL_SCOPES : [scope];
        const matches = [];
        for (const s of searchScopes) {
            for (const hash of yield candidatesForScope(s, session)) {
                if (hash.startsWith(normalized))
                    matches.push({ scope: s, hash });
            }
        }
        const deduped = dedupeByScope(matches);
        if (deduped.length === 0)
            throw new Error(`Unknown hash prefix '#${normalized}'`);
        if (deduped.length === 1)
            return deduped;
        const uniqueHashes = new Set(deduped.map((m) => m.hash));
        if (uniqueHashes.size === 1)
            return deduped;
        const lines = deduped.map((m) => `  ${m.scope.padEnd(7)} => ${m.hash}`).join('\n');
        throw new Error(`Ambiguous alias prefix '#${normalized}' (${deduped.length} matches):\n${lines}\n`
            + 'Retry with a longer prefix or \\alias <scope> <name> #...');
    });
}
function dedupeByScope(matches) {
    const seen = new Set();
    const out = [];
    for (const m of matches) {
        if (seen.has(m.scope))
            continue;
        seen.add(m.scope);
        out.push(m);
    }
    return out;
}
function candidatesForScope(scope, session) {
    return __awaiter(this, void 0, void 0, function* () {
        if (scope === 'key') {
            if (session.keyVault === undefined)
                return [];
            return session.keyVault.list().map((k) => k.keyId);
        }
        if (scope === 'version')
            return collectVersionOpHashes(session.workspace.roots);
        const kind = scope === 'db' ? 'database' : scope;
        return session.workspace.roots.list(kind).map((r) => r.id);
    });
}
export function collectVersionOpHashes(roots) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, e_1, _b, _c;
        const hashes = [];
        for (const root of roots.list()) {
            if (root.object === undefined)
                continue;
            const dag = yield root.object.getScopedDag();
            try {
                for (var _d = true, _e = (e_1 = void 0, __asyncValues(dag.loadAllEntries())), _f; _f = yield _e.next(), _a = _f.done, !_a; _d = true) {
                    _c = _f.value;
                    _d = false;
                    const entry = _c;
                    hashes.push(entry.hash);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = _e.return)) yield _b.call(_e);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        return hashes;
    });
}
export function aliasLabel(scope, hash, session) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        if (scope === 'key') {
            return (_b = (_a = session.keyVault) === null || _a === void 0 ? void 0 : _a.list().find((k) => k.keyId === hash)) === null || _b === void 0 ? void 0 : _b.label;
        }
        if (scope === 'version')
            return rootNameForVersionHash(hash, session.workspace.roots);
        return (_c = session.workspace.roots.get(hash)) === null || _c === void 0 ? void 0 : _c.name;
    });
}
export function rootNameForVersionHash(hash, roots) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const root of roots.list()) {
            if (root.object === undefined)
                continue;
            const dag = yield root.object.getScopedDag();
            if ((yield dag.loadEntry(hash)) !== undefined)
                return root.name;
        }
        return undefined;
    });
}
